/* eslint-disable max-lines */
import dayjs from 'dayjs';

import Game from '~/models/Game';

// @Todo: Merge these two different api service into one

import MigratedReportsAPIService from '~/services/MigratedReportsAPIService';
import GameAPIService from '~/services/apis/GameAPIService';
import LocalStorage from '~/services/LocalStorage';
import i18n from '~/services/i18n';
import Constants from '~/config/Constants';
import { getFormattedTimeInMinAndSec } from '~/utils/TimeUtils';
import QLogger from '~/services/QLogger';
import { getCountableQuestions } from '~/utils/filterQuestionsUtil';
import $featureFlags from '~/services/FeatureFlagsService';
import Analytics from '~/services/EventLoggerService';
import HotjarService, { HotjarEvents } from '~/services/HotjarService';
import get from 'lodash/get';
import useGameAccommodationsStore from '~/stores/gameAccommodations';

const GAME_END_STATES = ['ended', 'completed', 'stopped'];
const GAME_START_STATES = ['waiting', 'running', 'paused'];
const QUESTIONS_INITIAL_FILTERS = {
  sortType: 'order',
  sortOrder: 'asc',
};
export const PLAYERS_INITIAL_FILTERS = {
  sortOrder: 'desc',
  sortType: 'accuracy',
  attemptType: 'best',
};
export const SUBGROUP_INTIAL_FILTERS = {
  sortOrder: 'asc',
  sortType: 'name',
};
export const API_STATUSES = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success',
};
const SECONDS_IN_EIGHTEEN_MONTHS = 60 * 60 * 24 * 30 * 18;
const REDEMPTION_TOGGLE_KEY_NAME = 'qz-redempt-toggle';
export const REPORT_FILTERS_KEY_NAME = 'reportFilters';

/* --- Private functions --- */
function getResponseFilterPreviousSavedContext() {
  const defaultContext = {
    dismissed: false, // For toggle info tooltip
    toggleOn: true, // For toggle button state
  };
  const item = LocalStorage.getItem(REDEMPTION_TOGGLE_KEY_NAME);
  return item || defaultContext;
}

function saveResponseFilterContext(context) {
  const currentContext = getResponseFilterPreviousSavedContext();
  const contextToSave = { ...currentContext, ...context };
  LocalStorage.setItem(REDEMPTION_TOGGLE_KEY_NAME, contextToSave);
}

function initialResponseFilterValue() {
  const context = getResponseFilterPreviousSavedContext();
  return context.toggleOn ? Constants.ReportsResponseFilterValues.FINAL : Constants.ReportsResponseFilterValues.FIRST;
}

function isGameOnlySlidesUtil(gameInfo) {
  return gameInfo.totalAnswerableQuestions === 0 && gameInfo.questions.length !== 0;
}

export function filterPlayersBySelectedGroup(players, filter) {
  if (filter === 'all') {
    return players;
  }

  return players.filter(player => player.groupIds.includes(filter));
}

export function sortAndOrder(list, sortType, sortOrder) {
  let result = [...list];
  if (sortType === 'accuracy') { // Use points sort first then accuracy sort in case of accuracy sort selected
    result = sortAndOrder(result, 'points', sortOrder);
  }
  result.sort((a, b) => {
    if (sortType === 'firstName') {
      let leftValue = '';
      let rightValue = '';
      if (a.firstName && b.firstName) {
        leftValue = a.firstName;
        rightValue = b.firstName;
      } else if (a.firstName) {
        leftValue = a.firstName;
        rightValue = b.lastName;
      } else if (b.firstName) {
        leftValue = a.lastName;
        rightValue = b.firstName;
      } else {
        leftValue = a.lastName;
        rightValue = b.lastName;
      }
      return ((leftValue || '').trim().toLowerCase()).localeCompare((rightValue || '').trim().toLowerCase());
    }

    if (sortType === 'lastName') {
      let leftValue = '';
      let rightValue = '';
      if (a.lastName && b.lastName) {
        leftValue = a.lastName;
        rightValue = b.lastName;
      } else if (a.lastName) {
        leftValue = a.lastName;
        rightValue = b.firstName;
      } else if (b.lastName) {
        leftValue = a.firstName;
        rightValue = b.lastName;
      } else {
        leftValue = a.firstName;
        rightValue = b.firstName;
      }
      return ((leftValue || '').trim().toLowerCase()).localeCompare((rightValue || '').trim().toLowerCase());
    }

    if (sortType === 'points') {
      return Number.parseFloat(a.marks) > Number.parseFloat(b.marks) ? 1 : -1;
    }

    return a[sortType] > b[sortType] ? 1 : -1;
  });

  if (sortOrder === 'desc') {
    result.reverse();
  }
  return result;
}

export function findPlayerSelectedAttemptAggregation(player, playerAggregationsList, attemptType, selectedResponseFilter) {
  const attemptId = player[`${attemptType}AttemptId`];
  if (!attemptId) {
    return {};
  }

  const playerAgg = playerAggregationsList
    .find(playerAgg => playerAgg.attemptGroupId === player.attemptGroupId); // filter by attemptGroupId i.e. player
  const selectedAttemptAgg = playerAgg?.playerAttempts
    .find(attempt => attempt.attemptId === attemptId); // filter by attemptId i.e best, first, last, etc
  const responses = selectedAttemptAgg?.responses;

  if (selectedResponseFilter === Constants.ReportsResponseFilterValues.FIRST) {
    return responses[0];
  }

  return responses[responses.length - 1]; // choose the last response of all the responses of the questions
}

function transformPlayers(players, playerAggregations, isGameOnlySlide, selectedResponseFilter) {
  const userDefaultFilters = JSON.parse(LocalStorage.getItem(REPORT_FILTERS_KEY_NAME));
  const playerFilters = { ...PLAYERS_INITIAL_FILTERS, ...userDefaultFilters };
  const result = players.map(player => ({
    attemptGroupId: player.attemptGroupId,
    bestAttemptId: player.bestAttemptId,
    firstAttemptId: player.firstAttemptId,
    lastAttemptId: player.lastAttemptId,
    attemptIds: player.attemptIds,
    displayName: player.displayName,
    firstName: player.firstName,
    lastName: player.lastName,
    type: player.type,
    userId: player.userId,
    latestAttemptAt: player.latestAttemptAt, // used to sort by submission time
    groupIds: player.groupIds,
    attempts: player.attempts.sort((a, b) => (new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime() ? 1 : -1)),
    assignment: player.assignment,
    notStarted: !player.hasStarted,
    testGameActivitiesCount: playerAggregations?.testGameActivites?.total || 0, // Ideally agg should be used but right now coming as 0
    // testGameActivitiesCount: player.attempts.find((attempt) => attempt.id === player[`${PLAYERS_INITIAL_FILTERS.attemptType}AttemptId`])?.testGameActivities?.length || 0, // Temp
    ...findPlayerSelectedAttemptAggregation(player, playerAggregations, playerFilters.attemptType, selectedResponseFilter),
  }));
  return sortAndOrder(result, isGameOnlySlide ? 'latestAttemptAt' : playerFilters.sortType, playerFilters.sortOrder);
}

export function transformPlayerAggregations(playerAggregations, game, playersList) {
  return playerAggregations.map(player => ({
    ...player,
    playerAttempts: player.playerAttempts.map((pAttempt) => {
      const total = (playersList.find(playerItem => playerItem.attemptIds.includes(pAttempt.attemptId)).attempts
        .find(attempt => attempt.id === pAttempt.attemptId).questions?.length) || game.totalAnswerableQuestions;
      return {
        ...pAttempt,
        responses: pAttempt.responses.map((response) => {
          const {
            correct, yetToBeGraded, partial, incorrect, ungraded, timedout,
          } = response.distribution;
          const sum = (correct + yetToBeGraded + partial + incorrect + ungraded + timedout);
          const unattempted = (total - sum) > 0 ? (total - sum) : 0;
          return {
            ...response,
            accuracy: Math.round(response.accuracy * 100),
            score: Math.round(response.score),
            marks: ((response.marks * 10) / 10 === Math.round(response.marks))
              ? Math.round(response.marks)
              : (response.marks?.toFixed(1) || 0),
            distribution: {
              ...response.distribution,
              total,
              unattempted,
            },
            totalPossibleMarks: Math.round(pAttempt.totalPossibleMarks || game.totalPossibleMarks || 0),
            timeTaken: Math.round(response.timeTaken / ((response.responses || 1) * 1000)),
          };
        }),
      };
    }),
  }));
}

function transformQuestionAggregations(distribution) {
  const {
    correct, incorrect, partial, timedout, ungraded, total, yetToBeGraded,
  } = distribution;
  if (ungraded) {
    return {
      attempted: ungraded,
      unattempted: (total - ungraded) > 0 ? (total - ungraded) : 0,
      total,
      correct: correct + yetToBeGraded,
    };
  }
  const sum = (correct + incorrect + partial + timedout + yetToBeGraded);
  return {
    correct: correct + yetToBeGraded,
    incorrect,
    partial,
    timedout,
    unattempted: (total - sum) > 0 ? (total - sum) : 0,
    total,
  };
}

function getChildOrderDescription(question, idToIndexMap = {}) {
  if (question?.comprehensionQuestionIds?.length === 1) {
    const firstChildIndex = idToIndexMap[question.comprehensionQuestionIds[0]];
    return `Q[${firstChildIndex + 1}]`;
  }
  if (question?.comprehensionQuestionIds?.length > 1) {
    const firstChildIndex = idToIndexMap[question?.comprehensionQuestionIds?.[0]];
    const lastChildIndex = idToIndexMap[question?.comprehensionQuestionIds?.[question?.comprehensionQuestionIds?.length - 1]];
    return `[Q${firstChildIndex + 1} - Q${lastChildIndex + 1}]`;
  }
}

export function transformQuestions(questions, questionAggregations, totalPlayerAttemptsCount, selectedResponseFilter) {
  // #idToIndexMap countable questions is a list without meta questions. example of a meta question is comprehension.
  const idToIndexMap = getCountableQuestions(questions).reduce((acc, question, index) => ({
    ...acc,
    [question._id]: index,
  }), {});

  const result = questions
    .filter(question => ![Constants.SlideTypes.CONTENT_SLIDE_V1, Constants.SlideTypes.CONTENT_SLIDE_V2].includes(question.type))
    .map((question, questionIndex) => {
      const questionAggregation = questionAggregations.find(questionAgg => questionAgg.questionId === question._id);
      const { responses, options } = questionAggregation;
      let selectedAgg = responses[responses.length - 1];
      let selectedOptionsAgg = options[options.length - 1];

      if (selectedResponseFilter === Constants.ReportsResponseFilterValues.FIRST) {
        selectedAgg = responses[0];
        selectedOptionsAgg = options[0];
      }

      return {
        order: questionIndex + 1,
        childOrderDescription: getChildOrderDescription(question, idToIndexMap),
        childOrder: (idToIndexMap[question._id] + 1) || -1,
        _id: question._id,
        type: question.type,
        structure: question.structure,
        standardIds: question.standardIds || [],
        comprehensionQuestionId: question.comprehensionQuestionId,
        comprehensionTimestamp: question.comprehensionTimestamp,
        comprehensionQuestionIds: question.comprehensionQuestionIds,
        time: question.time, // assigned time for question
        topics: question.topics,
        ver: question.ver,
        accuracy: Math.round(selectedAgg.accuracy * 100),
        timeTaken: Math.round(selectedAgg.timeTaken / ((selectedAgg.responses || 1) * 1000)), // Avg.Time Taken
        questionDistribution: transformQuestionAggregations({
          ...selectedAgg.distribution,
          total: questionAggregation.totalPossibleAttempts ?? totalPlayerAttemptsCount,
        }),
        questionAudioMetrics: selectedAgg.audioMetrics,
        optionsAggregations: selectedOptionsAgg.distribution,
      };
    });
  return sortAndOrder(result, QUESTIONS_INITIAL_FILTERS.sortType, QUESTIONS_INITIAL_FILTERS.sortOrder);
}

export function transformResponses(responses) {
  return responses.map(response => ({
    _id: response._id,
    gameId: response.gameId,
    playerId: response.playerId,
    questionId: response.questionId,
    attempt: response.attempt,
    marks: (((response.marks * 10) / 10 === Math.round(response.marks)) ? Math.round(response.marks) : response.marks?.toFixed(1)),
    result: response.result,
    time: Math.round(response.time / 1000),
    formattedTime: getFormattedTimeInMinAndSec(response.time), // Human readable format eg 1m 12s
    answer: response.answer,
    response: response.response,
    metadata: response.metadata,
    grace: response.grace,
    audioEvaluation: response.audioMetrics,
  }));
}

function transformSubgroups(params, options = {}) {
  const { game, players, subgroupsAgg } = params;
  const {
    selectedResponseFilter = Constants.ReportsResponseFilterValues.FIRST,
    filters,
  } = options;
  const { sortType, sortOrder } = filters;
  const groupsInfo = game.groupsInfo || {};
  if (!groupsInfo?.differentiation?.use) {
    return [];
  }

  const { subgroupsMap = {}, playerToSubgroupsMap = {} } = groupsInfo.differentiation || {};
  const userIdToPlayer = players.reduce((acc, player) => {
    acc[player.userId] = player;
    return acc;
  }, {});
  const subgroupIdToAgg = subgroupsAgg.reduce((acc, agg) => {
    acc[agg.subGroupId] = agg;
    return acc;
  }, {});
  const subGroupToPlayers = Object.keys(playerToSubgroupsMap).reduce((acc, userId) => {
    const subgroupIds = playerToSubgroupsMap[userId];
    const player = userIdToPlayer[userId];
    subgroupIds.forEach((subgroupId) => {
      if (!acc[subgroupId]) {
        acc[subgroupId] = [];
      }
      acc[subgroupId].push(player);
    });
    return acc;
  }, {});

  const sgs = Object.keys(subgroupsMap).map(((subgroupId) => {
    const sg = subgroupsMap[subgroupId];
    const agg = subgroupIdToAgg[subgroupId];

    const totalPlayers = agg.attemptGroups.total;
    const completionRate = Math.round((agg.attemptGroups.completed / totalPlayers) * 100);
    const lastResponseAgg = agg.responses[agg.responses.length - 1];
    const firstResponseAgg = agg.responses[0];
    const responseAgg = selectedResponseFilter === Constants.ReportsResponseFilterValues.FIRST ? firstResponseAgg : lastResponseAgg;

    return {
      subgroupId,
      ...sg,
      players: subGroupToPlayers[subgroupId] || [],
      aggregations: {
        totalPlayers,
        completionRate,
        hasAccuracyGrowth: lastResponseAgg.accuracy !== firstResponseAgg.accuracy,
        firstPassAccuracy: Math.round(firstResponseAgg.accuracy * 100),
        lastPassAccuracy: Math.round(lastResponseAgg.accuracy * 100),
        accuracy: Math.round(responseAgg.accuracy * 100),
      },
    };
  }));

  return sgs.sort((a, b) => {
    const orderMultipler = sortOrder === 'asc' ? 1 : -1;
    let value = 0;
    if (sortType === 'name') {
      value = a.name.localeCompare(b.name);
    } else if (sortType === 'completion') {
      value = a.aggregations.completionRate - b.aggregations.completionRate;
    } else if (sortType === 'accuracy') {
      value = a.aggregations.accuracy - b.aggregations.accuracy;
    }
    return value * orderMultipler;
  });
}

/* --- Store definition --- */

function state() {
  const userDefaultFilters = JSON.parse(LocalStorage.getItem(REPORT_FILTERS_KEY_NAME));
  const playerFilters = { ...PLAYERS_INITIAL_FILTERS, ...userDefaultFilters };
  return {
    game: Game(),
    players: [],
    playerAttempts: [],
    questions: [],
    groups: [],
    subgroups: [],
    standards: [],
    responses: [],
    aggregations: {
      game: undefined,
      players: [],
      questions: [],
      groups: [],
      standards: [],
      playerStandards: [],
    },
    gameName: '',
    filteredPlayers: [],

    selectedEvaluateIds: {
      questionId: undefined,
      playerAttemptGroupId: undefined,
      responseId: undefined,
      attemptId: undefined,
    },
    questionsFilters: QUESTIONS_INITIAL_FILTERS,
    playerFilters,
    subgroupsFilters: SUBGROUP_INTIAL_FILTERS,
    selectedGroupFilter: 'all', // values can be ['all', <group-id>,..]
    selectedResponseFilter: '', // [Constants.ReportsResponseFilterValues.FIRST, Constants.ReportsResponseFilterValues.FINAL]

    classesAssignmentInProgress: false,
    gc: {
      flag: true,
      assignments: [],
      failedGroups: [],
      failedGroupsErrors: [],
    },
    attemptedLMSList: [],
    lastEditedQuestionId: '',

    apiStatuses: {
      game: API_STATUSES.LOADING,
      questions: API_STATUSES.LOADING,
      standards: API_STATUSES.LOADING,
      allResponses: API_STATUSES.LOADING,
    },
    uiState: {
      scrolledBelowTab: false,
    },

    gameAPILastUpdatedAt: new Date().getTime(),
    gameAccommodationsModalSource: null,
    showAccommodationsAfterAssign: false,
  };
}

const getters = {
  game: state => state.game,
  players: state => state.filteredPlayers,
  rawPlayers: state => state.players,
  questions: state => state.questions,
  responses: state => state.responses,
  aggregations: state => state.aggregations,
  playerAttempts: state => state.playerAttempts,
  subgroups: state => state.subgroups,
  playerAggregations: state => state.aggregations.players,
  selectedEvaluateIds: state => state.selectedEvaluateIds,
  questionsFilters: state => state.questionsFilters,
  playerFilters: state => state.playerFilters,
  subgroupsFilters: state => state.subgroupsFilters,
  selectedResponseFilter: state => state.selectedResponseFilter,
  selectedGroupFilter: state => state.selectedGroupFilter,
  questionIds: state => state.game.questions,
  standards: state => state.standards,
  standardAggregations: state => state.aggregations.standards,
  playerStandardAggregations: state => state.aggregations.playerStandards,
  apiStatuses: state => state.apiStatuses,
  gameId: state => state.game._id,
  gameCode: state => state.game.code,
  gameQuizOptions: state => state.game.quizOptions,
  totalAnswerableQuestionsInGame: state => state.game.totalAnswerableQuestions || state.game.questions?.length || 0,
  totalQuestionsAttemptedInGame: state => state.game.totalQuestions,
  lastEditedQuestionId: state => state.lastEditedQuestionId,
  gameName: state => state.gameName,
  gameAPILastUpdatedAt: state => state.gameAPILastUpdatedAt,
  showGameAccommodationsModal: state => state.gameAccommodationsModalSource !== null,
  gameAccommodationsModalSource: state => state.gameAccommodationsModalSource,
  getResponsesBy: (state, getters) => (attemptId, questionId, useResponseFilter = true, filterTimedOut = false) => {
    let filteredResponses = state.responses.filter((response) => {
      let matched = true;
      if (attemptId && attemptId !== response.playerId) {
        matched = false;
      }
      if (questionId && questionId !== response.questionId) {
        matched = false;
      }

      if (useResponseFilter) {
        if (getters.isFirstPassResponseFilterSelected && response.attempt > 0) {
          matched = false;
        }
      }

      return matched;
    });

    if (filterTimedOut) {
      filteredResponses = filteredResponses.filter(response => response.state !== 'timedOut');
    }

    return filteredResponses.sort((a, b) => a.attempt - b.attempt);
  },

  isQuestionEdited: state => (questionId) => {
    const questionEditMap = state.game.metadata?.editMap;
    if (!questionId || !questionEditMap) {
      return false;
    }

    return Object.values(questionEditMap.updated).includes(questionId);
  },

  isResponseAwardedGraceMarks: () => response => response?.grace,
  isResponseManuallyEvaluated: () => (response) => {
    const rescoredVals = response?.metadata?.rescored || [];
    const remarkedVals = response?.metadata?.remarked || [];
    return rescoredVals.length > 0 || remarkedVals.length > 0;
  },

  gameCompletionRate: (state, getters) => {
    if (state.game.totalPlayers === 0) {
      return 0;
    }

    const totalAttemptedQuestions = getters.players.reduce((count, player) => {
      const playerBestAttempt = player.attempts.find(attempt => attempt.id === player.bestAttemptId);
      if (playerBestAttempt) {
        if (getters.isGameOnlySlide) {
          return count + (playerBestAttempt.playerMetaData?.qIndex + 1) || 0;
        }

        return count + playerBestAttempt.totalAttempt;
      }
      return count;
    }, 0);

    const totalQuestions = getters.isAdaptiveGameType ? state.game.quizOptions.questionsPerAttempt : getters.totalAnswerableQuestionsInGame;
    return Math.round((totalAttemptedQuestions * 100) / (totalQuestions * getters.players.length));
  },

  gameTypeLabel: (state) => {
    const { gameType } = state.game;
    const settingType = Constants.GameSettingTypes;

    switch (gameType) {
      case settingType.TEAM:
        return i18n('Team');
      case settingType.ASYNC:
        return i18n('Assigned');
      case settingType.TEACHER_PACED:
        return i18n('Instructor-paced');
      case settingType.TEACHER_PACED_OFFLINE:
        return i18n('Paper mode');
      case Constants.GameMode.MYSTIC_PEAK:
        return i18n('Mastery Peak');
      case settingType.TEST:
        return i18n('Test');
      case settingType.CHALLENGE:
        return i18n('Challenge');
      case settingType.PRESENTATION_ASYNC:
        return i18n('Assigned');
      default:
        return i18n('Live');
    }
  },

  isTypeEligibleForDifferentiation: (state) => {
    const { gameType } = state.game;
    const settingType = Constants.GameSettingTypes;
    return [
      settingType.ASYNC,
      settingType.LIVE,
      settingType.TEST,
    ].includes(gameType);
  },

  showSubgroupsTab: (state, getters) => {
    return getters.isTypeEligibleForDifferentiation && state.accommodationsEnabled;
  },

  canEditAccommodations: (state, getters) => {
    return getters.isTypeEligibleForDifferentiation && state.accommodationsEnabled && (getters.isGameRunning || getters.isGamePaused);
  },

  quizContentType: (state) => {
    const presentationGameTypes = [
      Constants.GameSettingTypes.PRESENTATION_ASYNC,
      Constants.GameSettingTypes.PRESENTATION_TEACHER_PACED,
    ];
    const { gameType } = state.game;
    return presentationGameTypes.includes(gameType)
      ? Constants.ContentType.PRESENTATION
      : Constants.ContentType.QUIZ;
  },
  gameAccuracy: (state) => {
    const responseAggregations = state.aggregations.game?.responses || [];
    const agg = state.selectedResponseFilter === Constants.ReportsResponseFilterValues.FIRST
      ? responseAggregations[0]
      : responseAggregations[responseAggregations.length - 1];

    if (agg) {
      return Math.round(agg.accuracy * 100);
    }
    return 0;
  },

  // Returns game accuracy considering first question attempts only. This is for cases
  // where multiple attempts exists for a question for a player
  gameFirstPassAccuracy: (state) => {
    const responseAggregations = state.aggregations.game?.responses || [];

    if (responseAggregations.length) {
      return Math.round(responseAggregations[0].accuracy * 100);
    }

    return 0;
  },

  // Returns game accuracy considering final question attempt. This is for cases
  // where multiple attempts exists for a question for a player
  gameFinalPassAccuracy: (state) => {
    const responseAggregations = state.aggregations.game?.responses || [];

    if (responseAggregations.length) {
      return Math.round(responseAggregations[responseAggregations.length - 1].accuracy * 100);
    }

    return 0;
  },
  isFirstPassResponseFilterSelected: state => state.selectedResponseFilter === Constants.ReportsResponseFilterValues.FIRST,
  isFinalPassResponseFilterSelected: state => state.selectedResponseFilter === Constants.ReportsResponseFilterValues.FINAL,
  masteryAchievedPlayerCount: (_state, getters) => {
    const goal = getters.gameMasteryGoal;
    return getters.players.filter((player) => {
      const playerAgg = findPlayerSelectedAttemptAggregation(player, getters.playerAggregations, 'best', Constants.ReportsResponseFilterValues.FINAL);
      return playerAgg.accuracy >= goal;
    }).length;
  },

  isMasteryModeGame: (state) => {
    const { quizOptions } = state.game;
    return Boolean(quizOptions.masteryMode && quizOptions.masteryMode.goal);
  },
  isMysticPeakGame: state => state.game.gameType === Constants.GameMode.MYSTIC_PEAK,
  isRedemptionQuizGame: state => state.game.quizOptions.hasRedemptionPass === Constants.GameOptionsValues.HasRedemptionPass.YES,
  hasSubgroups: state => state.subgroups.length > 0,
  hasSubGroupWithRedemption: (state, getters) => {
    const subgroups = getters.subgroups;
    return subgroups.some(subgroup => subgroup.accommodations.redemptionQuestion === true);
  },
  isRepetitionGameMode: (_state, getters) => (
    getters.isMasteryModeGame
      || getters.isMysticPeakGame
      || getters.isRedemptionQuizGame
  ),
  gameMasteryGoal: (state, getters) => {
    const { quizOptions } = state.game;

    if (getters.isMasteryModeGame) {
      return quizOptions.masteryMode?.goal || 0;
    }

    if (getters.isMysticPeakGame) {
      return quizOptions.mysticPeak?.goal || 0;
    }

    return null;
  },

  isAdaptiveGameType: (state) => {
    const { adaptive, questionsPerAttempt } = state.game.quizOptions;

    return adaptive && questionsPerAttempt > 0;
  },

  isGameScheduled: (state) => {
    if (!state.game.scheduledAt) {
      return false;
    }
    const scheduleDate = state.game.scheduledAt;
    const hasGameStarted = dayjs(scheduleDate).isAfter(dayjs());
    return hasGameStarted;
  },

  gameScheduledTime: (state) => {
    const game = state.game || {};
    if (game.scheduledAt) {
      return dayjs(game.scheduledAt);
    }
    return null;
  },
  gameStartTime: (state) => {
    const game = state.game || {};
    const startTime = game.startedAt || game.scheduledAt || game.createdAt;
    if (!startTime) {
      return null;
    }
    return dayjs(startTime);
  },
  gameEndTime: (state) => {
    const game = state.game || {};

    if (game.reopened) {
      return dayjs(game.startedAt).add(game.expiry, 'seconds');
    }

    if (game.endedAt) {
      return dayjs(game.endedAt);
    }

    if (game.expiry && game.startedAt) {
      return dayjs(game.startedAt).add(game.expiry, 'second');
    }
  },
  hasGameStarted: (state, getters) => {
    const { gameScheduledTime, gameStartTime, isGameScheduled } = getters;
    const now = dayjs();

    if (GAME_START_STATES.includes(state.game.gameState) || GAME_END_STATES.includes(state.game.gameState)) {
      return true;
    }

    if (isGameScheduled) {
      return gameScheduledTime.isBefore(now);
    }

    if (!gameStartTime) {
      return false;
    }

    return gameStartTime.isBefore(now);
  },
  isGameRunning: state => state.game.gameState === 'running',
  isGamePaused: state => state.game.gameState === 'paused',
  hasGameFinished: (state) => {
    // check by game-state
    if (GAME_END_STATES.includes(state.game.gameState)) {
      return true;
    }

    // Not doing stricter check for now
    // const now = dayjs();

    // // check by computing end-time
    // if (state.game.endedAt) {
    //   return dayjs(state.game.endedAt).isBefore(now);
    // }

    // // check by expiry
    // let isExpired = false;
    // if (state.game.startedAt && state.game.expiry) {
    //   isExpired = dayjs(state.game.startedAt).add(state.game.expiry, 'second').isBefore(now);
    // }

    // return isExpired;

    return false;
  },
  hasGameEndedBeforeExpiry: (state) => {
    // const now = dayjs();

    // check by computing end-time
    if (state.game.endedAt && state.game.startedAt && state.game.expiry) {
      return dayjs(state.game.endedAt).isBefore(
        dayjs(state.game.startedAt).add(state.game.expiry, 'second'),
      );
    }

    return true;
  },
  isLiveGame: state => state.game.gameType === Constants.GameType.LIVE,
  isLiveMode: state => [
    Constants.GameMode.LIVE,
    Constants.GameMode.TEACHER_PACED,
    Constants.GameMode.TEST,
    Constants.GameMode.MYSTIC_PEAK,
    Constants.GameMode.TEAM,
  ].includes(state.game.gameType),
  isEditQuestionAllowed: state => [
    Constants.GameSettingTypes.ASYNC,
    Constants.GameMode.LIVE,
    Constants.GameMode.MYSTIC_PEAK,
    Constants.GameMode.TEAM,
    Constants.GameMode.TEST,
    Constants.GameSettingTypes.PRESENTATION_ASYNC,
  ].includes(state.game.gameType),
  isAsyncGame: state => [Constants.GameSettingTypes.ASYNC, Constants.GameSettingTypes.PRESENTATION_ASYNC]
    .includes(state.game.gameType), // includes quiz and pres both
  isGameEndTimeInfinity: (state) => {
    const { game } = state;
    const { expiry } = game;

    return (expiry >= SECONDS_IN_EIGHTEEN_MONTHS);
  },

  gcState: state => state.gc,
  classIds: state => state.game?.groupIds || [],
  hasClasses: state => state.game?.groupIds?.length > 0,
  classesInfo: state => state.game?.groupsInfo || {},
  assignments: state => state.game?.assignments || {},
  hasGCLClasses: (_state, getters) => {
    const gcl = getters.classesInfo.gcl || [];
    const hasGCL = getters.classesInfo?.hasGCL;
    return gcl.length > 0 || hasGCL;
  },

  assignedClassList: (state) => {
    const classroomsAssignedTo = state.groups || [];
    let dataFromGroupsInfoGCL = [];
    if (state.game?.groupsInfo?.gcl?.length > 0) {
      dataFromGroupsInfoGCL = state.game?.groupsInfo?.gcl;
    } else if (Object.keys(state.game?.groupsInfo?.assignedTo || {}).length > 0) {
      Object.keys(state.game?.groupsInfo?.assignedTo).forEach((gId) => {
        dataFromGroupsInfoGCL.push({
          groupId: gId,
          gclCourseId: state.game.groupsInfo.assignedTo[gId]?.courseId,
          lmsName: state.game.groupsInfo.assignedTo[gId]?.lmsName,
        });
      });
    }

    const dataMapFromGroupsInfoGCL = dataFromGroupsInfoGCL.reduce((accumulator, classroom) => {
      accumulator[classroom.groupId] = classroom;
      return accumulator;
    }, {});

    const assignedClassesWithCorrectLMS = classroomsAssignedTo.map((data) => {
      const classroom = JSON.parse(JSON.stringify(data));

      if (dataMapFromGroupsInfoGCL[classroom._id]) {
        classroom.lmsType = dataMapFromGroupsInfoGCL[classroom._id].lmsName;
      }

      if (!classroom.lmsType) {
        classroom.lmsType = 'quizizz';
      }

      return classroom;
    });

    return assignedClassesWithCorrectLMS;
  },

  attemptedLMSList: state => (state.attemptedLMSList ?? []),

  assignedClassCount: (_state, getters) => (getters.assignedClassList.length || 0),
  classSelectionList: (_state, getters) => {
    if (getters.assignedClassList.length > 0) {
      return [{
        title: 'All classes',
        _id: 'all',
        color: '',
      }, ...getters.assignedClassList.map(classroom => ({
        title: classroom.name,
        _id: classroom._id,
        color: classroom.color,
      }))];
    }
    return [];
  },
  assignedLMSList: (_state, getters) => {
    const classrooms = getters.assignedClassList;
    const lmsInvolved = classrooms.reduce((acc, classroom) => {
      const { lmsType } = classroom;
      if (!acc.includes(lmsType)) {
        acc.push(lmsType);
        return acc;
      }
      return acc;
    }, []);

    return lmsInvolved;
  },
  gameJoiningLinks: (state, getters) => {
    const groupIds = state.game.groupIds || [];
    const gameLinks = {};

    const gameLinksForExternalLMS = state.gameLinks || [];
    groupIds.forEach((groupId) => {
      const groupLink = gameLinksForExternalLMS.find(game => game.groupId === groupId);
      if (groupLink) {
        gameLinks[groupId] = groupLink;
        return;
      }

      // computing Quizizz classroom join links
      const host = window.location.origin;
      const gameJoinURL = `${host}/join?c=${getters.gameCode}`;
      gameLinks[groupId] = {
        groupId,
        web: gameJoinURL,
        mobile: gameJoinURL,
      };
    });

    return gameLinks;
  },
  lmsType: (_state, getters) => {
    if (getters.assignedLMSList.length === 1) {
      return getters.assignedLMSList[0];
    } if (getters.assignedLMSList.length === 0 || getters.game.groupIds?.length === 0) {
      return 'none';
    }

    return 'mixed';
  },
  isClassesAssignmentInProgress: state => state.classesAssignmentInProgress,
  isClassScheduledQuiz: state => state.game?.groupsInfo && !!state.game?.groupsInfo?.data?.startDate,
  hasClassScheduledQuizStarted: (state) => {
    if (state.game?.gameState === Constants.GameState.RUNNING) {
      return true;
    }

    return state.game?.groupsInfo && dayjs(state.game?.groupsInfo?.data?.startDate).isBefore(dayjs());
  },
  assignedParticipantsCount: state => state.aggregations.game?.attemptGroups.total,
  isGameOnlySlides: state => isGameOnlySlidesUtil(state.game),
  /**
   *
   * Screen size factor
   *
   * answers is-X-or-Larger expect for Tiny
   * TinyScreens in range of    0-360px
   * SmallScreens in range of   360-870px
   * MediumScreens in range of  870-1366px
   * DesktopScreens in range of 1366+px
   * largeScreens in range of   1920+ px
   *
   */

  // @TODO Kannojia - Get rid of this getter and handle it in better way
  // right now several components are using this but ideally we should be using
  // the existing defined media breakpoints.
  isMediumScreen: (_state, _getters, _rootState, rootGetters) => {
    const deviceWidth = rootGetters.deviceProps?.width || window?.innerWidth || 1024;

    // true for Screens in range of 870-1366px
    return deviceWidth > 870;
  },
  uiState: state => state.uiState,
  isGameWithoutMarks: state => [null, undefined, ''].includes(state.game.totalPossibleMarks),
  subscription: state => state.game.subscription,
  mandatoryRosterName: state => get(state, 'game.quizOptions.mandatoryRosterName', false),
};

const mutations = {
  setApiStatuses: (state, payload) => {
    state.apiStatuses = { ...state.apiStatuses, ...payload };
  },
  resetGameData: (state) => {
    state.game = Game();
    state.players = [];
    state.playerAttempts = [];
    state.questions = [];
    state.groups = [];
    state.gameLinks = [];
    state.standards = [];
    state.responses = [];
    state.aggregations = {
      game: undefined,
      players: [],
      questions: [],
      groups: [],
      standards: [],
      playerStandards: [],
      subGroups: [],
    };
    state.gameName = '';
    state.filteredPlayers = [];
    state.accommodationsEnabled = false;
  },
  setGameData: (state, payload) => {
    const gameAccommodations = useGameAccommodationsStore();
    const playerAggregations = transformPlayerAggregations(payload.aggregations.players, payload.gameInfo, payload.players);
    const isGameSlidesOnly = isGameOnlySlidesUtil(payload.gameInfo);
    state.game = payload.gameInfo;
    state.gameLinks = payload.gameLinks || [];
    state.gameName = payload.gameInfo.name || payload.gameInfo.quizName || '';
    state.players = transformPlayers(payload.players, playerAggregations, isGameSlidesOnly, state.selectedResponseFilter);
    state.filteredPlayers = filterPlayersBySelectedGroup(state.players, state.selectedGroupFilter);
    state.playerAttempts = payload.players.reduce((acc, player) => acc.concat(player.attempts.map(attempt => ({
      ...attempt,
      marks: ((attempt.marks * 10) / 10 === Math.round(attempt.marks))
        ? Math.round(attempt.marks)
        : (attempt.marks?.toFixed(1) || 0),
    }))), []);
    state.groups = payload.groups;
    state.subgroups = transformSubgroups({
      game: payload.gameInfo,
      players: state.players,
      subgroupsAgg: payload.aggregations.subGroups,
    }, {
      selectedResponseFilter: state.selectedResponseFilter,
      filters: state.subgroupsFilters,
    });
    state.aggregations.game = payload.aggregations.game;
    state.aggregations.players = playerAggregations;
    state.aggregations.groups = payload.aggregations.groups;
    state.aggregations.subGroups = payload.aggregations.subGroups;
    if (isGameSlidesOnly) {
      state.playerFilters = { ...state.playerFilters, sortType: 'latestAttemptAt' };
    }
    state.gameAPILastUpdatedAt = new Date().getTime();

    let accommodatedGroupIds = payload.gameInfo?.groupsInfo?.differentiation?.accommodatedGroupIds;
    if (!accommodatedGroupIds && payload.gameInfo?.groupsInfo?.differentiation?.use) {
      accommodatedGroupIds = payload.gameInfo?.groupIds || [];
    } else {
      accommodatedGroupIds = accommodatedGroupIds || [];
    }

    gameAccommodations.init({
      gameId: payload.gameInfo._id,
      groupIds: payload.gameInfo.groupIds || [],
      accommodatedGroupIds,
      students: state.players.reduce((acc, player) => {
        if (!player.userId) {
          return acc;
        }
        acc.push({
          userId: player.userId,
          name: player.displayName,
        });
        return acc;
      }, []),
    });

    state.accommodationsEnabled = gameAccommodations.isEnabled();

    if (state.showAccommodationsAfterAssign && state.accommodationsEnabled) {
      state.gameAccommodationsModalSource = 'after-assign';
      state.showAccommodationsAfterAssign = false;
    }
  },
  setQuestionsData: (state, payload) => {
    state.questions = transformQuestions(
      payload.questions,
      payload.aggregations.questions,
      state.playerAttempts.length,
      state.selectedResponseFilter,
    );
    state.aggregations.questions = payload.aggregations.questions;
  },
  setStandardsData: (state, payload) => {
    state.standards = payload.standards;
    state.aggregations.standards = payload.aggregations.standards;
    state.aggregations.playerStandards = payload.aggregations.playerStandards;
  },
  setResponsesData: (state, payload) => {
    const responses = transformResponses(payload.responses);
    state.responses = responses;
  },

  setSelectedEvaluateIds: (state, payload) => {
    state.selectedEvaluateIds = { ...state.selectedEvaluateIds, ...payload };
  },
  setQuestionsFilters: (state, payload) => {
    const { questions, questionsFilters } = state;
    const newFilters = { ...questionsFilters, ...payload };

    const { sortType, sortOrder } = newFilters;

    state.questionsFilters = newFilters;
    state.questions = sortAndOrder(questions, sortType, sortOrder);
  },

  setSubgroupsFilters: (state, payload) => {
    const { subgroupsFilters } = state;
    const newFilters = { ...subgroupsFilters, ...payload };
    state.subgroupsFilters = newFilters;
    state.subgroups = transformSubgroups({
      game: state.game,
      players: state.players,
      subgroupsAgg: state.aggregations.subGroups,
    }, {
      selectedResponseFilter: state.selectedResponseFilter,
      filters: newFilters,
    });
  },

  setPlayersFilters: (state, payload) => {
    const {
      playerFilters, players, aggregations, selectedResponseFilter,
    } = state;
    const newFilters = { ...playerFilters, ...payload };

    const { attemptType, sortType, sortOrder } = newFilters;

    const newPlayers = players.map(player => ({
      ...player,
      ...findPlayerSelectedAttemptAggregation(player, aggregations.players, attemptType, selectedResponseFilter),
    }));
    const filteredPlayers = filterPlayersBySelectedGroup(newPlayers, state.selectedGroupFilter);

    state.playerFilters = newFilters;
    state.filteredPlayers = sortAndOrder(filteredPlayers, sortType, sortOrder);
  },
  setGroupFilter: (state, value) => {
    if (value) {
      state.selectedGroupFilter = value;

      const { playerFilters, players, selectedGroupFilter } = state;
      const { sortType, sortOrder } = playerFilters;
      const filteredPlayers = filterPlayersBySelectedGroup(players, selectedGroupFilter);
      state.filteredPlayers = sortAndOrder(filteredPlayers, sortType, sortOrder);
    }
  },
  setResponseFilter: (state, value) => {
    if (value) {
      state.selectedResponseFilter = value;

      const {
        playerFilters,
        questionsFilters,
        players,
        playerAttempts,
        aggregations,
        questions,
        selectedGroupFilter,
        selectedResponseFilter,
      } = state;

      // Players
      const newPlayers = players.map(player => ({
        ...player,
        ...findPlayerSelectedAttemptAggregation(player, aggregations.players, playerFilters.attemptType, selectedResponseFilter),
      }));
      const filteredPlayers = filterPlayersBySelectedGroup(newPlayers, selectedGroupFilter);
      state.filteredPlayers = sortAndOrder(filteredPlayers, playerFilters.sortType, playerFilters.sortOrder);

      // Questions
      const newQuestions = transformQuestions(questions, aggregations.questions, playerAttempts.length, selectedResponseFilter);
      state.questions = sortAndOrder(newQuestions, questionsFilters.sortType, questionsFilters.sortOrder);

      // Save toggle state
      saveResponseFilterContext({ toggleOn: value === Constants.ReportsResponseFilterValues.FINAL });
    }
  },
  setClassesAssigmentInProgress: (state, value) => {
    state.classesAssignmentInProgress = value;
  },
  setGCState: (state, value) => {
    state.gc = value;
  },
  setAttemptedLMSList: (state, value) => {
    state.attemptedLMSList = value;
  },
  setGameName: (state, value) => {
    state.gameName = value;
  },
  setGameInfo: (state, value) => {
    state.game = value;
  },

  setLastEditedQuestionId: (state, value) => {
    state.lastEditedQuestionId = value;
  },
  setLoadingUiState: (state, payload) => {
    state.uiState = { ...state.uiState, ...payload };
  },

  setGameAccommodationsModalSource: (state, value) => {
    state.gameAccommodationsModalSource = value;
  },

  setAccommodationsAfterAssign: (state, value) => {
    state.showAccommodationsAfterAssign = value;
  },
};

const actions = {
  async resetAndFetchGameData({ commit, dispatch }, gameId) {
    try {
      commit('resetGameData');
      commit('setApiStatuses', { game: API_STATUSES.LOADING });
      const response = await MigratedReportsAPIService.fetchGame(gameId);
      commit('setGameData', response.data);
      dispatch('setResponseFilterInitialValue');
      commit('setApiStatuses', { game: API_STATUSES.SUCCESS });
      return response.data;
    } catch (e) {
      QLogger.log('Error at migratedReportStore.resetAndFetchGameData', e);
      commit('setApiStatuses', { game: API_STATUSES.ERROR });
    }
  },
  async fetchGameData({ state, commit, dispatch }, gameId) {
    if (state.game._id) {
      return state.game;
    }
    try {
      commit('setApiStatuses', { game: API_STATUSES.LOADING });
      const response = await MigratedReportsAPIService.fetchGame(gameId);
      commit('setGameData', response.data);
      dispatch('setResponseFilterInitialValue');
      commit('setApiStatuses', { game: API_STATUSES.SUCCESS });
      return response.data;
    } catch (e) {
      QLogger.log('Error at migratedReportStore.fetchGameData', e);
      commit('setApiStatuses', { game: API_STATUSES.ERROR });
      throw e;
    }
  },
  async fetchQuestionsData({ state, commit }, { gameId, questionIds }) {
    if (state.questions.length) {
      return;
    }
    try {
      commit('setApiStatuses', { questions: API_STATUSES.LOADING });
      const response = await MigratedReportsAPIService.fetchQuestions({ gameId, questionIds });
      commit('setQuestionsData', response.data);
      commit('setApiStatuses', { questions: API_STATUSES.SUCCESS });
      return response.data;
    } catch (e) {
      QLogger.log('Error at migratedReportStore.fetchQuestionsData', e);
      commit('setApiStatuses', { questions: API_STATUSES.ERROR });
    }
  },
  async fetchStandardsData({ commit }, gameId) {
    try {
      commit('setApiStatuses', { standards: API_STATUSES.LOADING });
      const response = await MigratedReportsAPIService.fetchStandards(gameId);
      commit('setStandardsData', response.data);
      commit('setApiStatuses', { standards: API_STATUSES.SUCCESS });
      return response.data;
    } catch (e) {
      QLogger.log('Error at migratedReportStore.fetchStandardsData', e);
      commit('setApiStatuses', { standards: API_STATUSES.ERROR });
    }
  },
  async fetchResponsesData({ commit }, { gameId, questionIds, playerIds }) {
    const fetchAllResponses = !questionIds?.length && !playerIds?.length;

    try {
      if (fetchAllResponses) {
        commit('setApiStatuses', { allResponses: API_STATUSES.LOADING });
      }
      const response = await MigratedReportsAPIService.fetchResponses({ gameId, questionIds, playerIds });
      commit('setResponsesData', response.data);
      if (fetchAllResponses) {
        commit('setApiStatuses', { allResponses: API_STATUSES.SUCCESS });
      }
      return response.data;
    } catch (e) {
      QLogger.log('Error at migratedReportStore.fetchResponsesData', e);
      if (fetchAllResponses) {
        commit('setApiStatuses', { allResponses: API_STATUSES.ERROR });
      }
    }
  },

  resetGameData({ commit }) {
    commit('resetGameData');
  },
  setSelectedEvaluateIds({ commit }, payload) {
    commit('setSelectedEvaluateIds', payload);
  },
  setQuestionsFilters({ commit }, payload) {
    commit('setQuestionsFilters', payload);
  },
  setPlayersFilters({ commit }, payload) {
    commit('setPlayersFilters', payload);
  },
  setGroupFilter({ commit }, payload) {
    commit('setGroupFilter', payload);
  },
  setSubgroupsFilters({ commit }, payload) {
    commit('setSubgroupsFilters', payload);
  },
  setResponseFilter({ commit }, payload) {
    commit('setResponseFilter', payload);
  },

  setGameAccommodationsModalSource({ commit }, payload) {
    commit('setGameAccommodationsModalSource', payload);
  },

  setAccommodationsAfterAssign({ commit }, payload) {
    commit('setAccommodationsAfterAssign', payload);
  },

  async sendClassroomMessage({ commit, getters }) {
    // Do not call for stopped or completed games
    if (getters.hasGameFinished) {
      return;
    }
    const isAssignedtoClasses = getters.classIds;
    if (isAssignedtoClasses && getters.hasGCLClasses) {
      const assignments = getters.assignments || {};
      const lmsNames = Object.keys(assignments);
      let allFound = true;
      const gcl = getters.classesInfo?.gcl || [];
      for (let i = 0; i < gcl.length; i++) {
        const classDetails = gcl[i];
        let found = false;

        for (let j = 0; j < lmsNames.length; j++) {
          const lmsName = lmsNames[j];
          const courseId = classDetails.gclCourseId;
          if (assignments[lmsName][courseId]) {
            found = true;
            break;
          }
        }
        allFound = allFound && found; // will become false if any found is false
        if (allFound === false) {
          break;
        }
      }

      if (!allFound || Object.keys(assignments)?.length === 0) {
        commit('setClassesAssigmentInProgress', true);
        const response = await GameAPIService.sendMessageToGCL(getters.gameId, lmsNames[0], false);
        commit('setClassesAssigmentInProgress', false);
        if (!response.success) {
          if (!response.data && response.error) {
            const groupIds = isAssignedtoClasses || [];
            const groupErr = {};
            groupIds.forEach((gId) => {
              groupErr[`${gId}`] = {
                message: response.error?.message,
                type: response.error?.error,
              };
            });
            response.data = {
              failedGroups: groupIds,
              failedGroupsErrors: {
                ...groupErr,
              },
            };
            commit('setGCState', { ...response.data, flag: false });
            commit('setAttemptedLMSList', isAssignedtoClasses || []);
          } else {
            commit('setGCState', { ...response.data, flag: false });
          }
        } else {
          if (!response?.data) {
            commit('setGCState', { ...response.data, flag: true });
          }
        }
      }
    }
  },

  async setLMSErrorList({ commit }, { errorResponse, selectedGroupList }) {
    commit('setGCState', { ...errorResponse?.data, flag: false });
    commit('setAttemptedLMSList', selectedGroupList);
  },

  async finishGame(_, gameId) {
    await GameAPIService.finishGame(gameId);
  },

  async updateGameName({ commit }, { gameId, newName, gameState }) {
    const response = await GameAPIService.updateGameName(gameId, newName, gameState);

    if (response.success) {
      commit('setGameName', newName);
    }

    return response;
  },

  async updateScheduledTime({ commit, state }, { gameId, newTime }) {
    const response = await GameAPIService.rescheduleGame({ roomHash: gameId, scheduledAt: newTime.toISOString() });
    if (response?.data?.success) {
      commit('setGameInfo', { ...state.game, scheduledAt: newTime.toISOString() });
    }
  },

  updateDeadlineTime({ commit, state }, { data }) {
    const gameData = state.game;
    const updatedGame = { ...gameData, expiry: data.newExpiry };

    commit('setGameInfo', updatedGame);
  },
  routeToLiveDashboard({ state }) {
    const { gameType, _id } = state.game;
    if (gameType === Constants.GameSettingTypes.LIVE) {
      if ($featureFlags().isEnabled(Constants.FeatureFlagsTypes.LIVE_DASH_MIGRATION)) {
        window.open(`/admin/activity/classic/${_id}`);
      } else {
        window.open(`/admin/quiz/${_id}/startV4`);
      }
    } else if (gameType === Constants.GameMode.MYSTIC_PEAK) {
      window.open(`/admin/mystic-peak/${_id}`);
    } else if (
      gameType === Constants.GameSettingTypes.PRESENTATION_TEACHER_PACED
      || gameType === Constants.GameSettingTypes.TEACHER_PACED
    ) {
      window.open(`/admin/presentation/${_id}/start`);
    } else if (gameType === Constants.GameSettingTypes.TEAM) {
      window.open(`/admin/quiz/${_id}/team`);
    } else if (gameType === Constants.GameSettingTypes.TEST) {
      window.open(`/admin/test/${_id}`);
    } else if (gameType === Constants.GameSettingTypes.TEACHER_PACED_OFFLINE) {
      window.open(`/admin/pm/${_id}`);
    }
  },
  async startScheduledGame({ state, dispatch }) {
    const gameId = state.game._id;
    // commit('setGameDetailsStatus', GAME_DETAILS_STATUS.LOADING);
    try {
      await GameAPIService.startScheduledGame(gameId);
      await dispatch('resetAndFetchGameData', gameId);
    } catch (error) {
      QLogger.log('Error while starting scheduled game.', error);
    }
  },

  setResponseFilterInitialValue({ commit, getters }) {
    // If game is of type mastery mode, mystic peak and redemption quiz
    // then set the initial value of response filter from user's saved preference
    let filterValue = Constants.ReportsResponseFilterValues.FINAL;
    if (getters.isMasteryModeGame || getters.isMysticPeakGame || getters.isRedemptionQuizGame) {
      filterValue = initialResponseFilterValue();
    }
    commit('setResponseFilter', filterValue);
  },

  setEditedQuestionId({ commit }, questionId) {
    commit('setLastEditedQuestionId', questionId);
  },
  setLoadingUiState({ commit }, payload) {
    commit('setLoadingUiState', payload);
  },
  async resumeLesson({ state }) {
    const gameId = state.game._id;
    HotjarService.triggerEvent(HotjarEvents.RESUME_LESSON);
    Analytics.logEvent('resumeLessonClickReportPage', {
      gameId: state.game._id,
      pausedAt: (new Date(state.game.pausedAt || 0)).toISOString(),
    });
    await GameAPIService.resumeLesson(gameId);
    setTimeout(() => {
      window.location.pathname = `/admin/presentation/${gameId}/start`;
    }, 500);
  },
};

const migratedReportStore = {
  state,
  getters,
  mutations,
  actions,
};

export default migratedReportStore;
